/**
 * 🎯 METRIC WIDGET (KPI)
 * ════════════════════════════════════════════════════════════════════════
 * 
 * PROPÓSITO:
 * Mostrar métricas numéricas clave (PRIMARY_INSIGHT o métricas secundarias).
 * 
 * REGLA VISUAL CLAVE:
 * PRIMARY_INSIGHT debe impactar visualmente sin lógica adicional:
 * - Número grande y protagonista
 * - Tipografía destacada
 * - Contexto pequeño abajo
 * - Ocupa todo el ancho (ya viene w:12 del backend)
 * 
 * Si el KPI no impacta visualmente, el sistema "parece inteligente" pero no lo es.
 */

import React from 'react';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { DataPoint } from '../../../../types/dashboard';

interface MetricWidgetProps {
  widget: {
    id: string;
    title: string;
    data: DataPoint[];
    metadata?: {
      aggregation?: string;
      metric?: string;
      format?: 'number' | 'currency' | 'percentage';
      decimals?: number;
    };
  };
  isPrimary?: boolean;
}

const MetricWidget: React.FC<MetricWidgetProps> = ({ widget, isPrimary = false }) => {
  
  // Valor principal
  const value = typeof widget.data?.[0]?.valor === 'number' 
    ? widget.data[0].valor 
    : parseFloat(String(widget.data?.[0]?.valor || 0)) || 0;
  
  // Formato según metadata
  const format = widget.metadata?.format || 'number';
  const decimals = widget.metadata?.decimals ?? 0;
  
  const formattedValue = formatMetricValue(value, format, decimals);
  
  // Subtítulo (título del widget)
  const subtitle = widget.title;
  
  return (
    <div className={`metric-widget ${isPrimary ? 'metric-widget-primary' : 'metric-widget-secondary'}`}>
      
      {/* Valor principal */}
      <div className={isPrimary ? 'metric-value-primary' : 'metric-value-secondary'}>
        {formattedValue}
      </div>
      
      {/* Subtítulo/contexto */}
      <div className={isPrimary ? 'metric-subtitle-primary' : 'metric-subtitle-secondary'}>
        {subtitle}
      </div>
      
      {/* TODO: Delta/comparación (futuro) */}
      {/* 
      <div className="metric-delta">
        <TrendingUp className="w-4 h-4 text-green-500" />
        <span className="text-sm text-green-500">+15% vs mes anterior</span>
      </div>
      */}
    </div>
  );
};

/**
 * Formatear valor según tipo
 */
function formatMetricValue(value: number, format: string, decimals: number): string {
  switch (format) {
    case 'currency':
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
    
    case 'percentage':
      return new Intl.NumberFormat('es-ES', {
        style: 'percent',
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value / 100);
    
    case 'number':
    default:
      return new Intl.NumberFormat('es-ES', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals
      }).format(value);
  }
}

export default MetricWidget;
